import React, { useState, useEffect, useCallback, useRef } from 'react'

// Types
interface Testimonial {
  id: string
  name: string
  location: string
  text: string
  rating: number
  service?: string
  role?: string
  image?: string | null
}

// Sample testimonials data (would come from your data file)
const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Sarah J.',
    location: 'Minneapolis, MN',
    text: 'Catalina transformed my chaotic pantry into a beautifully organized space. I can finally find everything I need! The attention to detail and thoughtful systems she created have made daily life so much easier.',
    rating: 5,
    service: 'Pantry Organization',
    image: null,
  },
  {
    id: '2',
    name: 'Michael C.',
    location: 'St. Paul, MN',
    text: 'The office organization service was a game-changer for my productivity. Every item has its place, and I can focus on what matters most. Highly recommend!',
    rating: 5,
    service: 'Office Organization',
    role: 'Business Owner',
    image: null,
  },
  {
    id: '3',
    name: 'Emily R.',
    location: 'Edina, MN',
    text: 'Professional, efficient, and the results exceeded my expectations. My closet has never looked better! Getting dressed is now a joy instead of a stress.',
    rating: 5,
    service: 'Closet Makeover',
    image: null,
  },
  {
    id: '4',
    name: 'David T.',
    location: 'Bloomington, MN',
    text: 'The team helped us prepare for our move. Decluttering before packing saved us so much time and stress. Their systems made unpacking in our new home a breeze.',
    rating: 5,
    service: 'Moving Prep',
    role: 'Family of 4',
    image: null,
  },
  {
    id: '5',
    name: 'Lisa A.',
    location: 'Woodbury, MN',
    text: 'Worth every penny! The maintenance plan keeps everything looking perfect months later. Catalina taught us habits that have transformed how we live in our space.',
    rating: 5,
    service: 'Whole Home + Maintenance',
    image: null,
  },
  {
    id: '6',
    name: 'James W.',
    location: 'Plymouth, MN',
    text: 'Our company break room went from disaster to delight. The team created systems that our employees actually follow. Morale has noticeably improved!',
    rating: 5,
    service: 'Commercial Organization',
    role: 'Office Manager',
    image: null,
  },
]

// Icon Components
const ChevronLeft: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
)

const ChevronRight: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
)

const Star: React.FC<{ className?: string; style?: React.CSSProperties }> = ({
  className,
  style,
}) => (
  <svg
    className={className}
    style={style}
    fill="currentColor"
    viewBox="0 0 20 20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
)

const Quote: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
  </svg>
)

const Pause: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

const Play: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

// Testimonial Card Component
interface TestimonialCardProps {
  testimonial: Testimonial
  isActive: boolean
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ testimonial, isActive }) => {
  return (
    <div
      className={`
        relative bg-white rounded-2xl p-8 h-full
        transition-all duration-500 ease-out
        ${isActive ? 'scale-100 opacity-100 animate-testimonialSlide' : 'scale-95 opacity-0'}
      `}
    >
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#F4F1ED]/50 to-transparent rounded-2xl" />

      {/* Quote mark */}
      <Quote className="absolute top-6 right-6 w-12 h-12 text-[#D5B8A8]/20 rotate-180" />

      <div className="relative z-10 flex flex-col h-full">
        {/* Rating Stars */}
        <div className="flex gap-1 mb-6">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-5 h-5 transition-all duration-300 ${
                i < testimonial.rating
                  ? 'text-[#D5B8A8] fill-[#D5B8A8]'
                  : 'text-gray-200 fill-gray-200'
              } ${isActive ? 'animate-starPop' : ''}`}
              style={{
                animationDelay: isActive ? `${i * 100}ms` : '0ms',
              }}
            />
          ))}
        </div>

        {/* Testimonial Text */}
        <blockquote className="flex-grow mb-8">
          <p className="text-[#596856] text-lg leading-relaxed italic">"{testimonial.text}"</p>
        </blockquote>

        {/* Service Tag */}
        {testimonial.service && (
          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-[#A8B8A3]/20 text-[#596856] text-sm font-medium rounded-full">
              {testimonial.service}
            </span>
          </div>
        )}

        {/* Client Info */}
        <div className="flex items-center pt-6 border-t border-[#A8B8A3]/20">
          {/* Avatar */}
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#B0A0B9]/30 to-[#D5B8A8]/30 flex items-center justify-center mr-4 shadow-sm">
            <span className="text-[#596856] font-semibold text-xl">
              {testimonial.name.charAt(0)}
            </span>
          </div>

          <div className="flex-grow">
            <p className="text-[#596856] font-semibold text-lg">{testimonial.name}</p>
            <div className="flex items-center gap-2 text-[#596856]/70 text-sm">
              <span>{testimonial.location}</span>
              {testimonial.role && (
                <>
                  <span className="text-[#A8B8A3]">•</span>
                  <span className="italic">{testimonial.role}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Main Enhanced Testimonials Component
const EnhancedTestimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Auto-advance logic
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        handleNext()
      }, 5000)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [currentIndex, isPaused])

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1))
    setTimeout(() => setIsTransitioning(false), 500)
  }, [isTransitioning])

  const handleNext = useCallback(() => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1))
    setTimeout(() => setIsTransitioning(false), 500)
  }, [isTransitioning])

  const goToSlide = useCallback(
    (index: number) => {
      if (isTransitioning || index === currentIndex) return
      setIsTransitioning(true)
      setCurrentIndex(index)
      setTimeout(() => setIsTransitioning(false), 500)
    },
    [currentIndex, isTransitioning]
  )

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe) {
      handleNext()
    }
    if (isRightSwipe) {
      handlePrevious()
    }
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrevious()
      if (e.key === 'ArrowRight') handleNext()
      if (e.key === ' ') {
        e.preventDefault()
        setIsPaused(!isPaused)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handlePrevious, handleNext, isPaused])

  const currentTestimonial = testimonials[currentIndex]

  return (
    <section
      id="testimonials"
      className="py-20 bg-gradient-to-b from-[#F4F1ED] to-white overflow-hidden"
    >
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-[#A8B8A3]/20 shadow-sm mb-6">
            <span className="w-2 h-2 bg-[#D5B8A8] rounded-full animate-pulse" />
            <span className="text-[#596856] text-sm font-medium tracking-wide uppercase">
              Client Stories
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl font-serif text-[#596856] mb-4">
            Transformations That Speak
          </h2>
          <p className="text-[#596856]/70 text-lg max-w-2xl mx-auto">
            Real experiences from real people who've discovered the joy of organized living
          </p>
        </div>

        {/* Testimonial Display */}
        <div className="relative">
          {/* Main Card Container */}
          <div
            className="relative mx-auto max-w-3xl"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="relative min-h-[400px] flex items-center justify-center">
              {/* Shadow cards for depth effect */}
              <div className="absolute inset-4 bg-[#A8B8A3]/10 rounded-2xl blur-xl" />
              <div className="absolute inset-2 bg-white/50 rounded-2xl blur-md" />

              {/* Active testimonial */}
              <div className="relative w-full">
                <TestimonialCard testimonial={currentTestimonial} isActive={true} />
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={handlePrevious}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 
                         w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center
                         text-[#596856] hover:bg-[#596856] hover:text-white transition-all duration-300
                         hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#A8B8A3]/50"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <button
              onClick={handleNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12
                         w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center
                         text-[#596856] hover:bg-[#596856] hover:text-white transition-all duration-300
                         hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#A8B8A3]/50"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-6 mt-12">
            {/* Dot Indicators */}
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`
                    relative w-2 h-2 rounded-full transition-all duration-300
                    focus:outline-none focus:ring-2 focus:ring-[#A8B8A3]/50
                    ${
                      index === currentIndex
                        ? 'w-8 bg-[#596856]'
                        : 'bg-[#A8B8A3]/40 hover:bg-[#A8B8A3]/60'
                    }
                  `}
                  aria-label={`Go to testimonial ${index + 1}`}
                >
                  {index === currentIndex && (
                    <span className="absolute inset-0 rounded-full bg-[#596856] animate-pulseDot" />
                  )}
                </button>
              ))}
            </div>

            {/* Play/Pause Button */}
            <button
              onClick={() => setIsPaused(!isPaused)}
              className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center
                         text-[#596856] hover:bg-[#596856] hover:text-white transition-all duration-300
                         focus:outline-none focus:ring-2 focus:ring-[#A8B8A3]/50"
              aria-label={isPaused ? 'Play testimonials' : 'Pause testimonials'}
            >
              {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
          </div>

          {/* Counter */}
          <div className="text-center mt-6">
            <span className="text-[#596856]/50 text-sm font-medium">
              {currentIndex + 1} / {testimonials.length}
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}

export default EnhancedTestimonials
